import { createApplicationHistory } from '@features/applicant/hooks/createApplicationHistory';
import { useInvitationReply } from '@features/applicant/hooks/useInvitationReply';
import { Application } from '@features/applications';
import MessagingModal from '@features/messaging/components/MessagingModal/MessagingModal';
import { getTimeZoneAbbreviation } from '@features/profile';
import { Close, Done, Message, RemoveRedEye } from '@mui/icons-material';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
// import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';

import AvailableSlots from '../AvailableSlots/AvailableSlots';
import WithdrawApplication from '../WithdrawApplication/WithdrawApplication';

type ScheduledInterviewModalProps = {
  isOpen: boolean;
  slots: boolean;
  handleClose: () => void;
  application: Application;
};

const ScheduledInterviewModal: FC<ScheduledInterviewModalProps> = ({
  isOpen,
  handleClose,
  application,
  slots,
}) => {
  const browserTimeZone = getTimeZoneAbbreviation();
  const [errorText, setErrorText] = useState('');
  const [showAvailableTime, setShowAvailableTime] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false);
  const { replyToInterviewRequest, isReplyToInterviewRequestLoading } =
    useInvitationReply({ setErrorText });
  createApplicationHistory({
    applicationId: application.id,
    type: 'INTERVIEW_SEEN',
    enabled: Boolean(application.calendar_item),
  });
  const handleSubmit = () => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          toast.success(data.message);
          handleClose();
        }
      },
    };
    if (application.calendar_item && application.calendar_item.id) {
      replyToInterviewRequest(
        {
          calendar_id:
            application.calendar_item && application.calendar_item.id,
          response: 2,
        },
        options,
      );
    }
  };

  const companyInfo =
    application?.calendar_block_invite?.company ||
    application?.calendar_item?.company;

  const physical_address_concate =
    companyInfo?.additional_info?.physical_interview_location ||
    `${companyInfo?.s_address1 || companyInfo?.s_address2 || ''} ${companyInfo?.s_city || ''} ${companyInfo?.s_state || ''} ${companyInfo?.s_zip || ''}`.trim();

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={() => {
              handleClose();
            }}
          >
            <Close />
          </IconButton>
          <br />
          {application.job_listing.company.s_logo_file && (
            <Avatar
              src={application.job_listing.company.s_logo_file}
              variant="square"
              sx={{
                borderRadius: 5,
                alignSelf: 'center',
                margin: 'auto',
                width: '60px',
                height: '60px',
              }}
            />
          )}
          <Box sx={{ p: 3 }}>
            <Typography variant="h4">
              {application.job_listing.company.s_name}
            </Typography>
            <Typography variant="body1">
              {application.job_listing.company.s_city}{' '}
              {application.job_listing.company.s_state},{' '}
              {application.job_listing.company.s_zip}
            </Typography>
          </Box>
          <Typography variant="h5">
            Interview requested:
            <br /> <b>{application?.job_listing?.s_title}</b>
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mb={2}>
            {errorText && <Alert severity="error">{errorText}</Alert>}

            {application?.application_actions &&
            application?.application_actions?.interview_message ? (
              <Typography
                sx={{
                  maxWidth: !application?.application_actions?.interview_message
                    ? '350px'
                    : 'auto',
                  textAlign: 'center',
                  alignSelf: 'center',
                }}
                textAlign="center"
                alignSelf="center"
                p={2}
                component="div" // so <p> tags render properly inside
                dangerouslySetInnerHTML={{
                  __html: application.application_actions.interview_message,
                }}
              />
            ) : (
              <Typography
                sx={{
                  maxWidth: !slots ? '350px' : 'auto',
                  textAlign: 'center',
                  alignSelf: 'center',
                }}
                textAlign="center"
                alignSelf="center"
                p={2}
              >
                {slots
                  ? 'You have been invited to attend an open interview session. Please choose a slot from the available times.'
                  : 'This employer has requested an interview. Please accept/choose an interview time, or message with your availability.'}
              </Typography>
            )}

            {application.calendar_item && (
              <>
                <Stack direction="row" spacing={2} alignSelf="center">
                  <CalendarMonthIcon />{' '}
                  <Typography>
                    {' '}
                    {dayjs(application.calendar_item.dt_start_time).format(
                      'MMMM DD, YYYY',
                    )}{' '}
                  </Typography>
                </Stack>
                <Stack alignSelf="center" direction="row" spacing={2}>
                  <AccessTimeFilledIcon />{' '}
                  <Typography>
                    {' '}
                    {dayjs(application.calendar_item.dt_start_time).format(
                      'hh:mm A',
                    )}{' '}
                    -{' '}
                    {dayjs(application.calendar_item.dt_end_time).format(
                      'hh:mm A',
                    )}{' '}
                    {browserTimeZone || ''}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                  textAlign="center"
                >
                  <strong>Interview type :</strong>
                  <span style={{ marginLeft: '4px' }}>
                    {(application &&
                      application?.calendar_item?.block_type === 1) ||
                    application?.calendar_block_invite?.block?.block_type == 1
                      ? 'In Person'
                      : 'Virtual'}
                  </span>
                </Typography>
              </>
            )}

            <Box mb={3}>
              {(application && application?.calendar_item?.block_type === 1) ||
              application?.calendar_block_invite?.block?.block_type === 1 ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '2px',
                    marginBottom: '10px',
                  }}
                >
                  <LocationOnOutlinedIcon
                    fontSize="medium"
                    sx={{ color: 'black' }}
                  />

                  <Box
                    sx={{
                      pl: 1,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {physical_address_concate}
                  </Box>
                </Box>
              ) : (
                <Typography
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '10px',
                    marginBottom: '8px',
                    width: '100%',
                    justifyContent: 'center',
                  }}
                  textAlign="center"
                  alignSelf="center"
                >
                  {/* <VideocamOutlinedIcon
                    sx={{ color: 'black' }}
                    fontSize="medium"
                  />

                  <a
                    style={{ paddingLeft: '5px', textDecoration: 'none' }}
                    target="_blank"
                    href={
                      application?.calendar_item?.company?.additional_info
                        ?.virtual_interview_link ||
                      application.company?.additional_info
                        ?.virtual_interview_link ||
                      application?.calendar_block_invite?.company
                        ?.additional_info?.virtual_interview_link
                    }
                    rel="noreferrer"
                  >
                    Join meeting
                  </a> */}
                </Typography>
              )}
            </Box>

            {!slots ? (
              <LoadingButton
                variant="contained"
                color="success"
                onClick={handleSubmit}
                sx={{ marginTop: 2 }}
                startIcon={<Done />}
                loading={isReplyToInterviewRequestLoading}
                disabled={isReplyToInterviewRequestLoading}
              >
                Confirm Interview Time
              </LoadingButton>
            ) : (
              <LoadingButton
                variant="contained"
                color="success"
                sx={{ marginTop: 2 }}
                startIcon={<RemoveRedEye />}
                onClick={() => setShowAvailableTime(true)}
              >
                View Avaliable Times
              </LoadingButton>
            )}
            <LoadingButton
              variant="outlined"
              color="error"
              sx={{ marginTop: 2 }}
              disabled={isReplyToInterviewRequestLoading}
              onClick={() => setIsWithdrawModalOpen(true)}
            >
              Withdraw Application
            </LoadingButton>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsMessagingModalOpen(true)}
              startIcon={<Message />}
              disabled={isReplyToInterviewRequestLoading}
            >
              Message
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {showAvailableTime && (
        <AvailableSlots
          application={application}
          handleClose={() => {
            handleClose();
            setShowAvailableTime(false);
          }}
          isOpen={showAvailableTime}
        />
      )}
      {isWithdrawModalOpen && (
        <WithdrawApplication
          isOpen={isWithdrawModalOpen}
          handleClose={() => {
            setIsWithdrawModalOpen(false);
            handleClose();
          }}
          applicationId={application.id}
          applicationName={application?.job_listing?.s_title}
        />
      )}
      <MessagingModal
        isOpen={isMessagingModalOpen}
        handleClose={() => setIsMessagingModalOpen(false)}
        application={application}
      />
    </>
  );
};

export default ScheduledInterviewModal;
